
import { Component } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import Editable from '@/mixins/Editable';
import VWrapper from '@/components/shared/VWrapper.vue';
import TableForm from '@/components/table/TableForm.vue';
import { namespace } from 'vuex-class';
import { Venue } from '@/interfaces/models/Venue';
import { Table } from '@/interfaces/models/Table';
import { Staff } from '@/interfaces/models/Staff';
import { Tag } from '@/interfaces/models/Tag';
import Filter from '@/interfaces/api/Filter';
import EditTableLoader from '@/components/table/EditTableLoader.vue';
import Notification from '@/mixins/Notification';
import StackedForm from '@/mixins/StackedForm';
import { Permission } from '@/enums/Permission';

const venue = namespace('venue');
const table = namespace('table');
const tag = namespace('tag');

@Component({
  components: { EditTableLoader, TableForm, VWrapper },
})
export default class EditTable extends mixins(Editable, Notification, StackedForm) {
  @table.Action('store') public store!: any;
  @table.Action('update') public update!: any;
  @table.Action('show') public show: any;
  @table.Action('fetchStaff') public getStaff!: any;
  @venue.Action('getCheckoutIntegration') public getCheckoutIntegration!: any;
  @tag.Action('setFilter') public setTagFilter!: (f: Filter) => void;
  @tag.Action('fetch') public getTags!: any;
  @table.State('active') public active: any;
  @table.State('staff') public staff!: Staff[];
  @venue.State('active') public venue!: Venue;
  @venue.State('checkoutIntegration') public checkoutIntegration!: any;
  @tag.State('items') public tags!: Tag[];

  public showStaffDialog: boolean = false;

  public $refs!: {
    form: InstanceType<typeof TableForm> & {
      getData: () => any;
      validate: () => Promise<boolean | boolean[]>;
      setValue: (key: string, value: any) => void;
    };
  };

  get table() {
    if (this.editing && this.active) {
      return this.active;
    }

    return null;
  }

  get title() {
    return this.editing ? 'table.edit' : 'table.create';
  }

  get lightspeedAuthorized() {
    return this.checkoutIntegration && !!this.checkoutIntegration?.lightspeed?.accessToken;
  }

  public async mounted() {
    if (!this.editing && !this.venue) {
      this.$router.push({ name: 'table.index', query: { ...this.$route.query } });
    }

    if (this.venue) {
      if (this.editing) {
        this.$startLoading('table');
        await this.show({ id: this.id });
        await this.loadCheckoutIntegration(this.venue);
        this.$stopLoading('table');
      }

      this.$startLoading('tags');
      this.setTagFilter({ venue: this.venue!._id });
      await this.getTags();
      this.$stopLoading('tags');
    }

    if (this.editing && !this.active) {
      this.$router.push({ name: 'table.index', query: { ...this.$route.query } });
    }
  }

  public async loadCheckoutIntegration(venue: Venue) {
    if (!this.$can(Permission.VENUE_CHECKOUTINTEGRATION_VIEW) || venue.checkoutIntegration !== 'lightspeed_k_series') {
      return;
    }

    await this.getCheckoutIntegration({
      venue: venue._id,
      integrationType: 'lightspeed_k_series',
    });
  }

  public async viewStaff() {
    if (!(this.venue && this.venue._id)) {
      return;
    }

    this.$startLoading('staff');
    await this.getStaff(this.venue._id);
    this.$stopLoading('staff');

    if (this.staff && this.staff.length > 0) {
      this.showStaffDialog = true;
    }
  }

  public selectStaff(staffId: number | string) {
    if (!staffId) {
      return this.closeStaffDialog();
    }

    this.$refs.form.setValue('lightspeedWaiterId', staffId);
    this.closeStaffDialog();
  }

  public closeStaffDialog() {
    this.showStaffDialog = false;
  }

  public save() {
    this.$refs.form.validate().then(async (res: boolean | boolean[]) => {
      if (this.isValid(res)) {
        if (this.editing) {
          await this.update({ ...this.$refs.form.getData(), venue: this.venue._id, id: this.active!._id });
        } else {
          await this.store({ ...this.$refs.form.getData(), venue: this.venue._id });
        }
        this.$router.push({ name: 'table.index', query: { ...this.$route.query } });
      } else {
        this.notifyError('notification.form');
      }
    });
  }
}
